import {ButtonWithIcon, CardSkeletonLoader, ErrorBoundary} from '@/components/atoms'
import {SaveCancelFooter, SectionTitle} from '@/components/molecules'
import {PageContainer} from '@/components/organisms'
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook'
import {handleGetActivity} from '@/services/activities'
import {useAppSelector, useAppDispatch} from '@/store'
// import { WARNING_MESSAGE } from '@/types/Activity'

import {setToastNotifications} from '@/store/slices/commonSlice'
import {getFormattedDate, getDisplayCurrency} from '@/utils/commons'
import {COLOR} from '@/utils/constants/colors'
import {Constants, localCurrencyText} from '@/utils/constants/commonConstants'
import {SIZE} from '@/utils/constants/globalStyle'
import {useEffect, useCallback, useState} from 'react'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {validate} from 'uuid'
import {useErrorHandler} from '@/hooks/useErrorHandler'
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert'

import {setActivityFormData} from '@/store/slices/activitySlice'
import {getModal} from '@/utils/CommonModal'
const ActivityReviewField = ({label, value}) => {
  let displayValue = value
  const upperCaseLabels = ['Activity Type', 'Receive Points', 'Order Type', 'Activity Name']
  if (upperCaseLabels.includes(label)) {
    displayValue = value?.toUpperCase()
  }
  if (label === 'Start Date' || label === 'End Date') {
    displayValue = getFormattedDate(value, false, '-', 'yyyy-mm-dd')
  }
  // If value is array (e.g., segments), join by comma for display
  if (Array.isArray(displayValue)) {
    displayValue = displayValue.join(', ')
  }
  // Format noOfDays as "X Days" for Point Expiration activity
  if (label === 'No Of Days') {
    label = 'Frequency'
    displayValue = `${value} Days`
  }
  return (
    <>
      {label === 'Start Date' && <SectionTitle label={'Availability'} />}
      {label === 'Local Currency' && <SectionTitle label={'Point Allocation'} />}
      {label === 'Number Of Cases' && <SectionTitle label={'Point Allocation (Number Of Cases)'} />}
      <div className="mt-4 flex w-full border-b border-gray-700 pb-2 md:w-1/2">
        <div className="w-1/2">
          <p className="pl-1 text-sm text-[#D0D0D0]">{label === 'Receive Points' ? 'Frequency' : label}</p>
        </div>
        <div className="w-1/2">
          <p className="pl-1 font-bold text-white">{displayValue}</p>
        </div>
      </div>
    </>
  )
}

const ActivityReview = ({cancelModalHandlers}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {handleApiError} = useErrorHandler()
  const {id} = useParams()
  const [searchParams] = useSearchParams()
  const reviewParam = searchParams.get('review')
  const isReviewPublished = reviewParam?.toLowerCase() === Constants.ReviewPublished.toLowerCase()
  const isReviewCompleted = reviewParam?.toLowerCase() === Constants.ReviewCompleted.toLowerCase()
  const isReviewInProgress = reviewParam?.toLowerCase() === Constants.InProgress.toLowerCase()

  const [loading, setLoading] = useState(false)
  const {getFormDataWithOrder} = useActivityReview()

  const getActivity = useCallback(
    async id => {
      try {
        const paramKey = validate(id) ? 'uuid' : 'id'
        setLoading(true)
        dispatch(setActivityFormData({})) // Reset form data before fetching new activity

        const response = await handleGetActivity(`${paramKey}=${id}`, false)
        setLoading(false)
        if (response?.data.success) {
          dispatch(setToastNotifications({show: true, message: 'Activity data fetched successfully', type: 'success'}))
          if (response?.data?.content?.length > 0) {
            const data = response?.data?.content[0].versionData
            // Extract activityId from the correct location
            const formDataWithOrder = getFormDataWithOrder(data)
            dispatch(setActivityFormData({...formDataWithOrder, status: data.status, activityId: data.activityId, [paramKey]: id}))
          }
        } else if (response?.data) {
          dispatch(setToastNotifications({show: true, message: 'Activity data fetched successfully', type: 'success'}))
          // Extract activityId from the correct location
          const formDataWithOrder = getFormDataWithOrder(response?.data)
          dispatch(setActivityFormData({...formDataWithOrder, status: response?.data.status, activityId: response?.data.activityId, [paramKey]: id}))
        } else {
          dispatch(setToastNotifications({show: true, message: 'Failed to fetch activity data', type: 'error'}))
          console.error('Failed to fetch activity data:', response?.message)
        }
      } catch (error) {
        setLoading(false)
        console.error('Error fetching activity data:', error)
        handleApiError(error)
      }
    },
    [dispatch, getFormDataWithOrder, handleApiError]
  )

  useEffect(() => {
    if (id) getActivity(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
  const {
    handlePublish,
    handleDraft,
    handleCancel: hookHandleCancel,
    activityFormData,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
    isNameValid,
    handleValidationModalClose,
  } = useActivityReview()

  // Use cancel handler from props if available (for AddActivityReview page)
  // Otherwise, use the hook's handler for direct usage
  const handleCancel = cancelModalHandlers?.handleCancel || hookHandleCancel

  const countryCode = useAppSelector(state => state.countryCode).countryCode
  const displayCurrency = getDisplayCurrency(countryCode)
  const isEditable = () => {
    return activityFormData.status?.toLowerCase() != Constants.Completed.toLowerCase()
  }

  const disableDraft = () => {
    return (
      activityFormData.status?.toLowerCase() == Constants.Published.toLowerCase() ||
      activityFormData.status?.toLowerCase() == Constants.InProgress.toLowerCase()
    )
  }

  const getCustomModal = type => {
    switch (type) {
      case 'NAME_VALIDATION_MODAL':
        return getModal({
          title: 'Duplicate Name Detected',
          text: (
            <>
              It looks like there's already an Activity with this name.
              <br />
              To avoid confusion, please enter a unique name before continuing.
            </>
          ),
          showPrimaryButton: true,
          primaryButtonText: 'Edit Name',
          onClickPrimaryButton: handleValidationModalClose,
          showSecondaryButton: false,
          showModal: !isNameValid,
          onCloseModal: handleValidationModalClose,
          hideCloseButton: true,
        })
      default:
        return null
    }
  }
  return (
    <ErrorBoundary>
      <>
        <PageContainer data-testid="activity-review" minHeight={'65vh'}>
          {loading && <CardSkeletonLoader count={3} />}
          {!loading && (
            <>
              <div data-testid="activity-review-content" className="flex items-center justify-between">
                <SectionTitle label={'Activity Setup'} />
                {isEditable() && (
                  <ButtonWithIcon
                    uiSchema={{
                      className: 'ml-8 flex items-center text-sm text-[#0069D7] border-none',
                      title: 'Edit',
                      size: SIZE.SMALL,
                      variant: COLOR.TERTIARY,
                      isDark: true,
                    }}
                    iconLeading="edit"
                    handleButton={() => navigate('/rewards/activity/add')}
                  />
                )}
              </div>
              {showToast && (
                <DuplicateAlert
                  type="info"
                  message={toastMessage}
                  show={true}
                  onCloseClick={handleToastClose}
                  handleClick={handleViewActivity}
                  buttonText="View Activity"
                />
              )}
              <>
                {Object.entries(activityFormData)
                  .filter(([key]) => {
                    // Exclude internal/metadata fields
                    const excludedKeys = [
                      'hasEndDate',
                      'status',
                      'name',
                      'uuid',
                      'id',
                      'allCustomers',
                      'excludeSegments',
                      'excludedSegments',
                    ]
                    return !excludedKeys.includes(key)
                  })
                  .map(([key, value]) => (
                    <ActivityReviewField
                      key={key}
                      label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      value={key === localCurrencyText && value ? `${value} ${displayCurrency}` : value || ''}
                    />
                  ))}
                {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL')}
              </>
            </>
          )}
          {/* <WarningLabel title={WARNING_MESSAGE.SCHEDULING_CONFLICT_DETECTED} /> */}
        </PageContainer>
        {!isReviewPublished && !isReviewCompleted && !isReviewInProgress && (
          <SaveCancelFooter
            isReview={true}
            handleCancel={handleCancel}
            nextAction={handlePublish}
            draftAction={handleDraft}
            disableDraft={disableDraft()}
          />
        )}
      </>
    </ErrorBoundary>
  )
}

export default ActivityReview
